import {
  Button,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  ListActions,
  NumberField,
  ReferenceField,
  SelectInput,
  SimpleList,
  TextField,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import permissions from "app/permissions";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import SecuredComponent from "server/security/SecuredComponent";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { MOBILE_BREAKPOINT, PreparatoryScheduleStatuses, PreparatoryScheduleStatusesTexts } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";
import PreparatoryScheduleExpanded from "./PreparatoryScheduleExpanded";

const listFilters = [
  <SelectInput
    key="status"
    source="status"
    label="Статус"
    alwaysOn
    choices={[
      { id: PreparatoryScheduleStatuses.New, name: PreparatoryScheduleStatusesTexts[PreparatoryScheduleStatuses.New] },
      {
        id: PreparatoryScheduleStatuses.Approved,
        name: PreparatoryScheduleStatusesTexts[PreparatoryScheduleStatuses.Approved],
      },
    ]}
  />,
];

const ListPreparatorySchedules = () => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const handleApprove = async id => {
    try {
      await dataProvider.customRequest(
        studentRequests.updatePreparatoryScheduleStatus(id),
        { status: PreparatoryScheduleStatuses.Approved },
        "PUT",
      );
      notify("Одобрихте успешно", { type: "success" });
      refresh();
    } catch (error) {
      notifyError(error);
      return false;
    }
  };

  const approveButton = record =>
    record.status === PreparatoryScheduleStatuses.New && (
      <ConfirmModal
        titleContent="Одобряване на разписание"
        onConfirm={() => handleApprove(record.id)}
        message="Сигурни ли сте, че искате да одобрите това разписание? Занятията се генерират само за одобрени разписания."
        triggerButton={
          <Button
            startIcon={<CheckCircleIcon />}
            size="small"
            sx={{ margin: 0.5 }}
            label="Одобри"
          />
        }
      />
    );

  return (
    <List
      title="Разписания подготвителна година"
      filters={listFilters}
      actions={
        <SecuredComponent permission={permissions.canManagePreparatorySchedules}>
          <ListActions />
        </SecuredComponent>
      }
      exporter={false}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <MobileListRow fields={[{ label: "Наименование", value: record?.name }]} />
          )}
          secondaryText={record => (
            <>
              <MobileListRow fields={[{ label: "Академична година", value: record?.academicYearName }]} />
              <MobileListRow
                fields={[
                  { label: "Статус", value: PreparatoryScheduleStatusesTexts[record.status] || "Неизвестен" },
                ]}
              />
              <MobileListRow fields={[{ label: "Дисциплини", value: record?.disciplinesCount }]} />
              <MobileListRow fields={[{ label: "Периоди", value: record?.periodsCount }]} />
              <SecuredComponent permission={permissions.canChangePreparatorySchedulesStatus}>
                {approveButton(record)}
              </SecuredComponent>
              <PreparatoryScheduleExpanded record={record} />
            </>
          )}
          tertiaryText={() => (
            <SecuredComponent permission={permissions.canManagePreparatorySchedules}>
              <EditButton label="" />
            </SecuredComponent>
          )}
        />
      ) : (
        <Datagrid
          expandSingle
          expand={<PreparatoryScheduleExpanded />}
          rowClick={false}
          bulkActionButtons={false}
        >
          <TextField
            source="name"
            label="Наименование"
            sortable={false}
          />
          <TextField
            source="academicYearName"
            label="Академична година"
            sortable={false}
          />
          <ReferenceField
            source="instructionLanguageId"
            reference={administrationRequests.getInstructionLanguagesSelect()}
            label="Език на обучение"
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <NumberField
            source="disciplinesCount"
            label="Дисциплини"
            sortable={false}
          />
          <NumberField
            source="periodsCount"
            label="Периоди"
            sortable={false}
          />
          <FunctionField
            label="Статус"
            sortable={false}
            render={record => PreparatoryScheduleStatusesTexts[record.status] || "Неизвестен"}
          />
          <SecuredComponent permission={permissions.canChangePreparatorySchedulesStatus}>
            <FunctionField render={approveButton} />
          </SecuredComponent>
          <SecuredComponent permission={permissions.canManagePreparatorySchedules}>
            <EditButton />
          </SecuredComponent>
        </Datagrid>
      )}
    </List>
  );
};

export default ListPreparatorySchedules;
